import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Building2, 
  Linkedin, 
  Target, 
  Activity,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Zap,
  Settings,
  ArrowUpRight
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { hasActiveSeat, fetchLiveness, type SeatLite } from "@/lib/accountLiveness";

import { useWorkspace } from "@/contexts/WorkspaceContext";

export default function AdminDashboard() {
  const { isActuallySubAdmin, isActuallySuperAdmin } = useWorkspace();
  const isSubAdminView = isActuallySubAdmin && !isActuallySuperAdmin;
  // Fetch platform overview stats — uses head:true count-by-status (no row payloads)
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["admin-platform-stats"],
    queryFn: async () => {
      const last24hIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const [
        workspaces,
        workspacesActive,
        workspacesInactive,
        clientsTotal,
        clientsActive,
        clientsDisconnected,
        clientsDisconnected24h,
        campaignsTotal,
        campaignsActive,
      ] = await Promise.all([
        supabase.from("workspaces").select("id", { count: "exact", head: true }),
        supabase.from("workspaces").select("id", { count: "exact", head: true }).eq("subscription_status", "active"),
        supabase.from("workspaces").select("id", { count: "exact", head: true }).neq("subscription_status", "active"),
        supabase.from("clients").select("id", { count: "exact", head: true }),
        supabase.from("clients").select("id", { count: "exact", head: true }).eq("status", "active"),
        supabase
          .from("clients")
          .select("id", { count: "exact", head: true })
          .eq("status", "disconnected")
          .not("unipile_account_id", "is", null)
          .eq("is_demo", false),
        supabase
          .from("clients")
          .select("id", { count: "exact", head: true })
          .eq("status", "disconnected")
          .not("unipile_account_id", "is", null)
          .eq("is_demo", false)
          .gte("last_status_check", last24hIso),
        supabase.from("campaigns").select("id", { count: "exact", head: true }),
        supabase.from("campaigns").select("id", { count: "exact", head: true }).eq("status", "active"),
      ]);

      // Live = connected + at least one active, unexpired seat (shared rule).
      const [{ data: liveClients }, { data: seats }] = await Promise.all([
        supabase.from("clients").select("id, workspace_id, status").eq("is_demo", false).limit(5000),
        supabase.from("account_subscriptions").select("client_id, status, current_period_end").limit(10000),
      ]);
      const seatsBy = new Map<string, SeatLite[]>();
      (seats || []).forEach((s: any) => s.client_id && seatsBy.set(s.client_id, [...(seatsBy.get(s.client_id) || []), s]));
      const liveWs = new Set<string>();
      let liveAccounts = 0;
      (liveClients || []).forEach((c: any) => {
        if (c.status === "disconnected" || !hasActiveSeat(seatsBy.get(c.id) || [])) return;
        liveAccounts++;
        if (c.workspace_id) liveWs.add(c.workspace_id);
      });
      const totalWs = workspaces.count || 0;

      // Blocked = in LinkedIn connection cooldown, for accounts that are still live.
      const { data: cooldownRows } = await (supabase as any)
        .from("account_connection_cooldowns")
        .select("client_id")
        .gt("stage", 0);
      const cooldownIds = ((cooldownRows || []) as any[]).map((r) => r.client_id);
      const live = await fetchLiveness(supabase, cooldownIds);
      const blockedAccounts = cooldownIds.filter((id: string) => live.has(id) && live.get(id) === null).length;

      return {
        totalWorkspaces: totalWs,
        activeWorkspaces: liveWs.size,
        inactiveWorkspaces: Math.max(0, totalWs - liveWs.size),
        totalClients: clientsTotal.count || 0,
        activeClients: liveAccounts,
        disconnectedClients: clientsDisconnected.count || 0,
        disconnectedClients24h: clientsDisconnected24h.count || 0,
        blockedAccounts,
        totalCampaigns: campaignsTotal.count || 0,
        activeCampaigns: campaignsActive.count || 0,
      };

    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Fetch recent activity
  const { data: recentActivity } = useQuery({
    queryKey: ["admin-recent-activity"],
    queryFn: async () => {
      const { data } = await supabase
        .from("activity_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(10);
      return data || [];
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Fetch system health (comprehensive)
  const { data: systemHealth } = useQuery({
    queryKey: ["admin-system-health"],
    queryFn: async () => {
      const now = new Date();
      const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      const lastHour = new Date(now.getTime() - 60 * 60 * 1000).toISOString();

      const [pendingJobs, processingJobs, failedJobs, recentErrors, completedLastHour] = await Promise.all([
        supabase.from("job_queue").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("job_queue").select("id", { count: "exact", head: true }).eq("status", "processing"),
        supabase.from("job_queue").select("id", { count: "exact", head: true }).eq("status", "failed").gte("created_at", last24h),
        supabase.from("action_log").select("id", { count: "exact", head: true }).eq("status", "failed").gte("timestamp", last24h),
        supabase.from("job_queue").select("id", { count: "exact", head: true }).eq("status", "completed").gte("executed_at", lastHour),
      ]);

      const failedCount = failedJobs.count || 0;
      const errorCount = recentErrors.count || 0;
      const totalIssues = failedCount + errorCount;

      return {
        pendingJobs: pendingJobs.count || 0,
        processingJobs: processingJobs.count || 0,
        recentFailures: failedCount,
        recentErrors: errorCount,
        completedLastHour: completedLastHour.count || 0,
        status: totalIssues > 100 ? "critical" : totalIssues > 50 ? "warning" : "healthy",
      };
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Render skeleton placeholders inline; no full-page spinner blocking other queries.
  const statsCardValue = (val: number | undefined) =>
    statsLoading ? <Skeleton className="h-7 w-16" /> : <div className="text-2xl font-bold tracking-tight">{val || 0}</div>;


  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">{isSubAdminView ? "Your Workspaces" : "Platform Administration"}</h1>
        <p className="text-sm text-muted-foreground">
          {isSubAdminView ? "Overview of your assigned workspaces, accounts and campaigns." : "Overview of all workspaces, accounts, and system health."}
        </p>
      </div>

      {/* Platform Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="shadow-sm hover:shadow-md transition-all duration-200 border-sidebar-border/60">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Workspaces</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsCardValue(stats?.activeWorkspaces)}
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <span>of {stats?.totalWorkspaces || 0} total</span>
              <span className="text-muted-foreground/60">•</span>
              <span>{stats?.inactiveWorkspaces || 0} inactive</span>
            </p>
          </CardContent>

        </Card>

        <Card className="shadow-sm hover:shadow-md transition-all duration-200 border-sidebar-border/60">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active LinkedIn Accounts</CardTitle>
            <Linkedin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsCardValue(stats?.activeClients)}
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <span>of {stats?.totalClients || 0} total</span>
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm hover:shadow-md transition-all duration-200 border-sidebar-border/60">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Running Campaigns</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsCardValue(stats?.activeCampaigns)}
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <span>of {stats?.totalCampaigns || 0} total</span>
            </p>
          </CardContent>
        </Card>

        <Link to="/admin/disconnected-accounts" className="block">
          <Card className={cn(
            "shadow-sm hover:shadow-md transition-all duration-200 border-sidebar-border/60 h-full",
            (stats?.disconnectedClients || 0) > 0 && "border-destructive/40",
          )}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Disconnected</CardTitle>
              <AlertTriangle className={cn(
                "h-4 w-4",
                (stats?.disconnectedClients || 0) > 0 ? "text-destructive" : "text-muted-foreground",
              )} />
            </CardHeader>
            <CardContent>
              {statsCardValue(stats?.disconnectedClients)}
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <span className={cn(
                  "font-medium",
                  (stats?.disconnectedClients24h || 0) > 0 ? "text-destructive" : "text-muted-foreground",
                )}>
                  +{stats?.disconnectedClients24h || 0} in 24h
                </span>
                <span className="text-muted-foreground/60">•</span>
                <span>View list</span>
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/blocked-accounts" className="block">
          <Card className={cn(
            "shadow-sm hover:shadow-md transition-all duration-200 border-sidebar-border/60 h-full",
            (stats?.blockedAccounts || 0) > 0 && "border-destructive/40",
          )}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Blocked Accounts</CardTitle>
              <AlertCircle className={cn(
                "h-4 w-4",
                (stats?.blockedAccounts || 0) > 0 ? "text-destructive" : "text-muted-foreground",
              )} />
            </CardHeader>
            <CardContent>
              {statsCardValue(stats?.blockedAccounts)}
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <span>LinkedIn cooldown</span>
                <span className="text-muted-foreground/60">•</span>
                <span>View list</span>
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* System Health Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-sidebar-border/60 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Job Queue Health
            </CardTitle>
            <CardDescription>Real-time processing metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Queue Status</p>
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "h-2.5 w-2.5 rounded-full animate-pulse",
                    systemHealth?.status === "healthy" ? "bg-emerald-500" : 
                    systemHealth?.status === "warning" ? "bg-amber-500" : "bg-destructive"
                  )} />
                  <span className="text-lg font-semibold capitalize">{systemHealth?.status || "Unknown"}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {systemHealth?.pendingJobs || 0} jobs pending
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Throughput (1h)</p>
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  <span className="text-lg font-semibold">{systemHealth?.completedLastHour || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Jobs completed</p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Error Rate (24h)</p>
                <div className="flex items-center gap-2">
                  <AlertCircle className={cn("h-4 w-4", (systemHealth?.recentFailures || 0) > 0 ? "text-destructive" : "text-muted-foreground")} />
                  <span className="text-lg font-semibold">{systemHealth?.recentFailures || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Failed jobs</p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-border/50 grid grid-cols-2 gap-4">
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm font-medium">Database</span>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Operational
                </Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm font-medium">Job Processor</span>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Running
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-sidebar-border/60 shadow-sm flex flex-col">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="h-4 w-4 text-primary" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col gap-3">
            <Link to="/admin/organizations" className="w-full">
              <Button variant="outline" className="w-full justify-between h-auto py-3 group hover:border-primary/30">
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  Manage Workspaces
                </span>
                <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
              </Button>
            </Link>
            {!isSubAdminView && (
            <Link to="/job-queue" className="w-full">
              <Button variant="outline" className="w-full justify-between h-auto py-3 group hover:border-primary/30">
                <span className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  Monitor Queue
                </span>
                <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
              </Button>
            </Link>
            )}
            {!isSubAdminView && (
            <Link to="/system-capacity" className="w-full">
              <Button variant="outline" className="w-full justify-between h-auto py-3 group hover:border-primary/30">
                <span className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  View Capacity
                </span>
                <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
              </Button>
            </Link>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Feed */}
      <Card className="border-sidebar-border/60 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            {isSubAdminView ? "Recent Activity" : "Recent Platform Activity"}
          </CardTitle>
          <CardDescription>{isSubAdminView ? "Latest actions in your workspaces" : "Latest actions across all workspaces"}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border/50">
            {recentActivity?.length ? (
              recentActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-8 w-8 rounded-full bg-muted/50 flex items-center justify-center">
                      <Activity className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{activity.action}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <span>{formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}</span>
                        {activity.entity_type && (
                          <>
                            <span>•</span>
                            <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                              {activity.entity_type}
                            </Badge>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {activity.workspace_id && (
                    <Link to={`/admin/organizations/${activity.workspace_id}`}>
                      <Button variant="ghost" size="sm" className="h-8 text-xs">View</Button>
                    </Link>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Activity className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No recent activity</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
